import axios from 'axios'
import { GetServerSideProps } from 'next'
import { ReactNode, useState } from 'react'
import { useAsync } from 'react-use'
import styled from 'styled-components'
import { z } from 'zod'
import { AddKolsToPredictiveReportModalProvider } from '@/components/common/modals/hooks/use-add-kols-to-predictive-report-modal'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailOverviewHeader from '@/components/kol/common/kol-detail-overview-header'
import KolDetailSection from '@/components/kol/common/kol-detail-section'
import AudienceAnalyses from '@/components/kol/detail/audience-analyses/audience-analyses'
import CooperationAndValueSection from '@/components/kol/detail/collaboration-and-value'
import CreativeContentAnalyses from '@/components/kol/detail/creative-content-analyses'
import EngagementAnalyses from '@/components/kol/detail/engagement-analyses'
import BasicInfo from '@/components/kol/detail/overview/basic-info'
import ContentDistribution from '@/components/kol/detail/overview/content-distribution'
import FollowerAnalysis from '@/components/kol/detail/overview/follower-analysis'
import PlatformMetricsSummary from '@/components/kol/detail/overview/platform-metrics-summary'
import Statistics from '@/components/kol/detail/overview/statistics'
import MentionedKolList from '@/components/kol/detail/similar-kols/mentioned-kol-list'
import SimilarKolList from '@/components/kol/detail/similar-kols/similar-kol-list'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { KolInfoProvider } from '@/components/kol/hooks/use-kol-info-context'
import useSectionTabs from '@/components/kol/hooks/use-section-tabs'
import KolDetailLayout from '@/components/kol/layout'
import { KOL_DETAIL_SECTION_TABS_HEIGHT } from '@/components/kol/navigation/section-tabs'
import { KOL_DETAIL_TOP_SUMMARY_HEIGHT } from '@/components/kol/navigation/top-summary'
import UnlockBanner from '@/components/kol/navigation/unlock-banner'
import TourLayout from '@/components/layout/tour-layout'
import { HeaderName } from '@/constants/header-name'
import routes from '@/hooks/use-authorization/routes'
import useCheckPageAvailable from '@/hooks/use-check-page-available'
import useDwellTracking from '@/hooks/use-dwell-tracking'
import useExitPageDwellTime from '@/hooks/use-exit-page-dwell-time'
import useKolDetailDuration from '@/hooks/use-kol-detail-duration'
import { useIntl } from '@/i18n/hooks/use-intl'
import { NextLayoutComponentType } from '@/types/next-layout-component-type'
import {
  applyKolSchemaModel,
  KolSchemaModel,
  KolSchemaRemote,
} from '@/types/schema/kol-schema'
import { ampli, VisitKolDetailProperties } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'
import { shouldShowContentDistribution } from '@/utils/detail/should-show-content-distribution'
import { getEnterpriseIdentityDomain, isLocal } from '@/utils/env'
import { camelizeKeys } from '@/utils/humps'
import logger from '@/utils/logger'

/**
 * @todo Move to routes
 */
const visitKolDetailPropertiesFromSchema =
  z.custom<VisitKolDetailProperties['from']>()

interface KolDetailForOpenGraphProps {
  infoForOpenGraph: KolSchemaModel
}

const KolDetail: NextLayoutComponentType<KolDetailForOpenGraphProps> = () => {
  const { formatMessage } = useIntl()
  const params = routes.kolId.useParams()
  const fragmentIdentifier = routes.kolId.useFragmentIdentifier()
  const searchParams = routes.kolId.useSearchParams()
  const setSearchParams = routes.kolId.useSetSearchParams()

  const [showMockData, setShowMockData] = useState(false)

  const {
    kolInfo,
    isLocked,
    isFetching: isFetchingKolInfo,
  } = useKolInfo(params.data?.kolId, searchParams.data?.kolDcIdHashed)

  const tabOptions = useSectionTabs(searchParams.data?.platform)

  const from = ((): VisitKolDetailProperties['from'] | undefined => {
    const parseResult = visitKolDetailPropertiesFromSchema.safeParse(
      searchParams.data?.from,
    )

    return parseResult.success ? parseResult.data : undefined
  })()

  useExitPageDwellTime((second: number): void => {
    if (kolInfo && searchParams.data?.platform) {
      ampli.exitKolDetail({
        from,
        keyword: searchParams.data?.keyword,
        kolCategory: kolInfo.customizedTagsNames,
        kolCountry: kolInfo.countryCode ?? undefined,
        kolDcId: searchParams.data?.kolDcIdHashed,
        kolId: kolInfo.id,
        kolName: kolInfo.name,
        platform: searchParams.data.platform,
        second,
        isUnlocked: !isLocked,
      })
    }
  })

  useDwellTracking((second: number, searchId: string): void => {
    if (kolInfo && searchParams.data?.platform) {
      ampli.exitKolDetail({
        from,
        keyword: searchParams.data?.keyword,
        kolCategory: kolInfo.customizedTagsNames,
        kolCountry: kolInfo.countryCode ?? undefined,
        kolDcId: searchParams.data?.kolDcIdHashed,
        kolId: kolInfo.id,
        kolName: kolInfo.name,
        platform: searchParams.data.platform,
        second,
        searchId,
        isUnlocked: !isLocked,
      })
    }
  })

  const platform = searchParams.success
    ? convertPlatformShortcode(searchParams.data.platform)
    : undefined

  const { duration, enable3MonthDuration } = useKolDetailDuration()

  const anchorOffset =
    KOL_DETAIL_SECTION_TABS_HEIGHT + KOL_DETAIL_TOP_SUMMARY_HEIGHT

  useCheckPageAvailable(params.success && searchParams.success)

  useAsync(async () => {
    if (!kolInfo || !searchParams.data?.platform || !searchParams.success) {
      return
    }

    try {
      await setSearchParams(
        {
          ...searchParams.data,
          isUnlocked: !isLocked,
        },
        fragmentIdentifier.data,
      )
    } catch (error) {
      logger.error(error)
    }
  }, [isLocked, kolInfo, searchParams.data?.platform, searchParams.success])

  const isFetching = isFetchingKolInfo || !platform?.shortcode

  return (
    <>
      {tabOptions.map((tab) => {
        if (tab.key === 'overview') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
                extraContent: (
                  <KolDetailOverviewHeader
                    duration={duration}
                    enable3MonthDuration={enable3MonthDuration}
                    hiddenActions={showMockData}
                    isFetchingKolInfo={isFetching}
                    isKolInfoLocked={isLocked}
                    kolInfo={kolInfo}
                    platform={platform}
                  />
                ),
              }}
              key={tab.key}
            >
              <BasicInfo
                isFetchingKolInfo={isFetching}
                keyword={searchParams.data?.keyword}
                kolInfo={kolInfo}
                platform={platform}
              />
              <Statistics
                duration={duration}
                isFetchingKolInfo={isFetching}
                isKolInfoLocked={isLocked}
                kolInfo={kolInfo}
                platform={platform}
                showMockData={showMockData}
              />
              {
                // 成效評價只支援 fb/ig/yt
                [
                  PlatformShortcode.Facebook,
                  PlatformShortcode.Instagram,
                  PlatformShortcode.YouTube,
                ].includes(platform.shortcode) && (
                  <PlatformMetricsSummary
                    duration={duration}
                    isFetchingKolInfo={isFetching}
                    isKolInfoLocked={isLocked}
                    kolInfo={kolInfo}
                    platform={platform}
                    showMockData={showMockData}
                  />
                )
              }
              <FollowerAnalysis
                duration={duration}
                isFetchingKolInfo={isFetching}
                isKolInfoLocked={isLocked}
                kolInfo={kolInfo}
                platform={platform}
                showMockData={showMockData}
              />
              {shouldShowContentDistribution(platform.shortcode) && (
                <ContentDistribution
                  duration={duration}
                  isFetchingKolInfo={isFetching}
                  isKolInfoLocked={isLocked}
                  kolInfo={kolInfo}
                  platform={platform}
                  showMockData={showMockData}
                />
              )}
            </KolDetailSection>
          )
        }

        if (tab.key === 'similarKols') {
          if (!kolInfo || !platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <SimilarKolList
                kolInfo={kolInfo}
                platform={platform}
                showMockData={showMockData}
              />
              {platform.shortcode === PlatformShortcode.Instagram && (
                <KolDetailDataBlock
                  colSpan={24}
                  header={{
                    title: `${platform.title} ${formatMessage({
                      id: 'kol:mentioned_influencers',
                    })}`,
                    tooltip: {
                      title: formatMessage({
                        id: 'kol:mentioned_brands_and_influencers_tooltip',
                      }),
                    },
                  }}
                  kolId={kolInfo.id}
                  kolMetrics='Related influencers'
                  kolName={kolInfo.name}
                  platform={platform.shortcode}
                >
                  <MentionedKolList
                    duration={duration}
                    kolDcIdHashed={kolInfo.dcIdHashed}
                    kolId={kolInfo.id}
                    kolName={kolInfo.name}
                    platform={platform.shortcode}
                    showMockData={showMockData}
                  />
                </KolDetailDataBlock>
              )}
            </KolDetailSection>
          )
        }

        if (tab.key === 'audienceAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <AudienceAnalyses
                isFetchingKolInfo={isFetching}
                isKolInfoLocked={isLocked}
                kolInfo={kolInfo}
                platform={platform}
                showMockData={showMockData}
              />
            </KolDetailSection>
          )
        }

        if (tab.key === 'cooperationAndValue') {
          if (!platform) {
            return null
          }

          return (
            <CooperationAndValueSection
              anchor={{ id: tab.key, offset: anchorOffset }}
              duration={duration}
              isFetchingKolInfo={isFetching}
              isKolInfoLocked={isLocked}
              key={tab.key}
              kolInfo={kolInfo}
              platform={platform}
              showMockData={showMockData}
            />
          )
        }

        if (tab.key === 'engagementAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <EngagementAnalyses
                duration={duration}
                isFetchingKolInfo={isFetching}
                isKolInfoLocked={isLocked}
                kolInfo={kolInfo}
                platform={platform}
                showMockData={showMockData}
              />
            </KolDetailSection>
          )
        }

        if (tab.key === 'creativeContentAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <CreativeContentAnalyses
              anchor={{ id: tab.key, offset: anchorOffset }}
              duration={duration}
              isFetchingKolInfo={isFetching}
              isKolInfoLocked={isLocked}
              key={tab.key}
              kolInfo={kolInfo}
              platform={platform}
              showMockData={showMockData}
            />
          )
        }

        return (
          <KolDetailSection
            header={{
              title: formatMessage({ id: tab.i18n }),
              anchor: { id: tab.key, offset: anchorOffset },
            }}
            key={tab.key}
          >
            <Box>{kolInfo?.name}</Box>
          </KolDetailSection>
        )
      })}
      {isLocked && (
        <UnlockBanner
          kolInfo={kolInfo}
          showMockData={showMockData}
          onClickShowMockData={(): void => setShowMockData((prev) => !prev)}
        />
      )}
    </>
  )
}

KolDetail.getLayout = (
  page,
  pageProps: KolDetailForOpenGraphProps,
): ReactNode => {
  return (
    <TourLayout>
      <KolInfoProvider value={{ infoForOpenGraph: pageProps.infoForOpenGraph }}>
        <AddKolsToPredictiveReportModalProvider>
          <KolDetailLayout>{page}</KolDetailLayout>
        </AddKolsToPredictiveReportModalProvider>
      </KolInfoProvider>
    </TourLayout>
  )
}

export const getServerSideProps: GetServerSideProps<
  KolDetailForOpenGraphProps
> = async (context) => {
  const { kolId } = context.query

  try {
    const infoResponse = await axios.get<KolSchemaRemote>(
      `${process.env.NEXT_PUBLIC_K6RSATELLITE_API_BASE_URL}/v1/kols/${kolId}/info`,
      isLocal(context.req?.headers.host)
        ? {
            headers: {
              [HeaderName.EnterpriseIdentityDomain]:
                getEnterpriseIdentityDomain(),
            },
          }
        : undefined,
    )
    const infoForOpenGraph: KolSchemaModel = applyKolSchemaModel(
      camelizeKeys(infoResponse.data) as KolSchemaRemote,
    )

    return {
      props: { infoForOpenGraph },
    }
  } catch (e) {
    logger.error(e, { ssr: true })
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }
}

const Box = styled.div`
  grid-column: span 24 / span 24;
  border-radius: 4px;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.04);
  background: ${({ theme }): string => theme.colors.base.grey.grey9};
  height: 500px;
  padding: 20px 30px 30px;
`

export default KolDetail
